import React from "react";
import { useNavigate } from "react-router";
import { arrayOf, bool, elementType, node, shape, string } from "prop-types";
import { Menu, MenuItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { StyledHeaderLink, headerMenuStyles } from "shared/theme/styles";

import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import googleAnalytics from "shared/analytics";

const BaseMenu = ({ MenuIcon = null, menuTitle = null, items, useRouterNavigation = true }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const navigate = useNavigate();

  const navigateToHref = (href) => {
    if (useRouterNavigation) {
      navigate(href);
      return;
    }

    window.location.assign(href);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    googleAnalytics.trackHeaderNav({ action: "menu", label: menuTitle });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const slug = menuTitle
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .concat("-menu");

  return (
    <>
      <StyledHeaderLink
        anchorEl={anchorEl}
        onClick={handleClick}
        aria-label={menuTitle}
        aria-controls={anchorEl ? slug : undefined}
        aria-haspopup="true"
      >
        <MenuIcon />
        <Typography component="span">{menuTitle}</Typography>
        <ExpandMoreRoundedIcon />
      </StyledHeaderLink>
      <Menu
        id={slug}
        sx={headerMenuStyles}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            "aria-labelledby": "resources-button",
          },
        }}
      >
        {items.map((item, i) => (
          <MenuItem
            key={i}
            onClick={() => {
              googleAnalytics.trackHeaderNav({
                action: "menu",
                label: item.googleAnalyticsLabel,
              });
              navigateToHref(item.href);
              setAnchorEl(null);
            }}
            sx={{
              "& .MuiListItemIcon-root": {
                color: "secondary.main",
              },
              "& .MuiListItemText-root": {
                color: "secondary.main",
              },
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.text}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

BaseMenu.propTypes = {
  MenuIcon: elementType,
  menuTitle: string,
  items: arrayOf(
    shape({
      href: string.isRequired,
      icon: node,
      text: string,
      googleAnalyticsLabel: string,
    }),
  ).isRequired,
  useRouterNavigation: bool,
};

export default BaseMenu;
